import { useEffect } from "react";
import { motion } from "framer-motion";
import { MapPin, Phone, Clock, Bus, TramFront, CreditCard, CalendarClock, CalendarCheck } from "lucide-react";
import { BRAND } from "@/lib/data";
import { useContent } from "@/lib/content";
import { getActiveHours, getUpcomingHolidays } from "@/lib/hours";
import { Overline } from "@/components/Overline";
import { Seo } from "@/components/Seo";

const RESERVE_URL = "https://reservierung.lacontadina-dresden.de";

const ease = [0.16, 1, 0.3, 1];
const WEEKDAYS = ["Sonntag", "Montag", "Dienstag", "Mittwoch", "Donnerstag", "Freitag", "Samstag"];

export default function Contact() {
  const c = useContent();
  const t = c.text.contact;
  const b = c.brand;
  const today = WEEKDAYS[new Date().getDay()];
  const season = getActiveHours(c);
  const upcoming = getUpcomingHolidays(c.holidays);

  useEffect(() => {
    if (window.location.hash === "#reserve") {
      setTimeout(() => document.getElementById("reserve")?.scrollIntoView({ behavior: "smooth" }), 300);
    }
  }, []);

  return (
    <div data-testid="contact-page" className="bg-cream">
      <Seo page="contact" />
      <section className={`pt-44 pb-16 relative ${c.images.contactHero ? "" : "bg-sand grain"}`}>
        {c.images.contactHero && (
          <>
            <div className="absolute inset-0"><img src={c.images.contactHero} alt="" className="w-full h-full object-cover" data-testid="contact-hero-img" /></div>
            <div className="absolute inset-0 bg-ink/60" />
          </>
        )}
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 text-center">
          <Overline light={!!c.images.contactHero} className="!flex justify-center">Kontakt</Overline>
          <h1 className={`font-serif font-light text-5xl sm:text-6xl lg:text-7xl tracking-tight ${c.images.contactHero ? "text-cream" : "text-ink"}`}>{t.title}</h1>
          <p className={`font-light mt-5 max-w-xl mx-auto ${c.images.contactHero ? "text-cream/85" : "text-ink-soft"}`}>{t.intro} Auch telefonisch unter {b.phone}.</p>
        </div>
      </section>

      <section className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-5 gap-16">
          <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7, ease }} className="lg:col-span-2 space-y-10">
            <div>
              <Overline>So finden Sie uns</Overline>
              <ul className="space-y-6 mt-2">
                <li className="flex gap-4">
                  <MapPin className="text-wine shrink-0 mt-1" size={22} strokeWidth={1.4} />
                  <div>
                    <p className="text-xs uppercase tracking-[0.2em] text-ink-muted mb-1">Adresse</p>
                    <p className="text-ink">Pizzeria e Trattoria „La Contadina"</p>
                    <a href={BRAND.mapUrl} target="_blank" rel="noreferrer" data-testid="contact-map-link" className="text-ink hover:text-wine transition-colors">{b.address}</a>
                  </div>
                </li>
                <li className="flex gap-4">
                  <Phone className="text-wine shrink-0 mt-1" size={22} strokeWidth={1.4} />
                  <div>
                    <p className="text-xs uppercase tracking-[0.2em] text-ink-muted mb-1">Telefon</p>
                    <a href={BRAND.phoneHref} data-testid="contact-phone-link" className="text-ink hover:text-wine transition-colors">{b.phone}</a>
                  </div>
                </li>
                <li className="flex gap-4">
                  <Bus className="text-wine shrink-0 mt-1" size={22} strokeWidth={1.4} />
                  <div>
                    <p className="text-xs uppercase tracking-[0.2em] text-ink-muted mb-1">Anfahrt</p>
                    <p className="text-ink text-sm">{BRAND.transit.bus}</p>
                    <p className="text-ink text-sm flex items-center gap-1.5"><TramFront size={14} className="text-gold" />{BRAND.transit.tram}</p>
                  </div>
                </li>
              </ul>
            </div>

            <div>
              <div className="flex items-center gap-3 mb-4">
                <Clock className="text-wine" size={22} strokeWidth={1.4} />
                <p className="text-xs uppercase tracking-[0.2em] text-ink-muted">Öffnungszeiten · {season.label}</p>
              </div>
              <ul className="space-y-3 border-t border-gold/20 pt-4">
                {season.hours.map((h) => {
                  const isToday = h.day === today;
                  return (
                    <li key={h.day} className={`flex justify-between gap-4 text-sm ${isToday ? "font-medium" : ""}`}>
                      <span className={isToday ? "text-orange-500" : "text-ink"}>{h.day}{isToday && " · Heute"}</span>
                      <span className={`text-right ${isToday ? "text-orange-500" : h.closed ? "text-wine" : "text-ink-soft"}`}>{h.time}</span>
                    </li>
                  );
                })}
              </ul>
              <p className="text-xs text-gold-dark mt-3">{b.hoursNote}</p>
              <p className="text-xs text-ink-muted mt-2 flex items-center gap-2"><CreditCard size={14} className="text-gold" />{b.payment}</p>
            </div>

            {upcoming.length > 0 && (
              <div data-testid="holidays-box" className="border border-gold/30 bg-sand/60 p-5">
                <div className="flex items-center gap-3 mb-3">
                  <CalendarClock className="text-wine" size={20} strokeWidth={1.4} />
                  <p className="text-xs uppercase tracking-[0.2em] text-ink-muted">Sonderöffnungszeiten</p>
                </div>
                <ul className="space-y-2">
                  {upcoming.map((h, i) => (
                    <li key={i} className="flex justify-between gap-4 text-sm" data-testid={`holiday-${i}`}>
                      <span className="text-ink">
                        {new Date(h.date).toLocaleDateString("de-DE", { weekday: "short", day: "2-digit", month: "2-digit" })}
                        {h.label ? ` · ${h.label}` : ""}
                      </span>
                      <span className={`text-right ${h.closed ? "text-wine font-medium" : "text-ink-soft"}`}>{h.closed ? "Geschlossen" : h.time}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="overflow-hidden border border-gold/20">
              <iframe title="Karte La Contadina Dresden" src="https://maps.google.com/maps?q=Reisewitzer%20Stra%C3%9Fe%2049%2C%2001159%20Dresden&t=&z=16&ie=UTF8&iwloc=&output=embed" className="w-full h-64" loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
            </div>
            {c.streetView && (
              <div className="mt-6" data-testid="streetview-block">
                <Overline className="mb-3">360° Rundgang</Overline>
                <div className="overflow-hidden border border-gold/20 rounded">
                  <iframe title="360° Street View La Contadina" src={c.streetView} data-testid="streetview-iframe" className="w-full h-72" loading="lazy" referrerPolicy="no-referrer-when-downgrade" allowFullScreen />
                </div>
                <p className="text-xs text-ink-muted mt-2">Ziehen Sie im Bild, um sich in 360° umzusehen.</p>
              </div>
            )}
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7, delay: 0.1, ease }} id="reserve" className="lg:col-span-3 bg-white border border-gold/20 p-8 sm:p-12 md:p-16 scroll-mt-28 flex flex-col items-center justify-center text-center">
            <CalendarCheck className="text-wine mb-6" size={48} strokeWidth={1.2} />
            <Overline className="!flex justify-center">Tisch reservieren</Overline>
            <h2 className="font-serif font-light text-3xl sm:text-4xl text-ink leading-tight mb-4">Ihr Tisch wartet auf Sie</h2>
            <p className="text-ink-soft font-light leading-relaxed mb-8 max-w-md">
              Reservieren Sie bequem online in nur wenigen Schritten. Der Button öffnet unser Reservierungssystem in einem neuen Fenster. An Feiertagen gelten ggf. Sonderöffnungszeiten.
            </p>
            <a href={RESERVE_URL} target="_blank" rel="noreferrer" data-testid="contact-reserve-btn" className="btn-pop bg-wine text-cream hover:bg-wine-dark px-10 py-4 uppercase text-xs tracking-[0.25em] inline-flex items-center gap-2">
              <CalendarCheck size={18} /> Jetzt Tisch reservieren
            </a>
            <p className="text-xs text-ink-muted mt-5">oder telefonisch unter <a href={BRAND.phoneHref} className="text-wine hover:underline">{b.phone}</a></p>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
